import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { AgGridReact, CustomFilterProps, useGridFilter } from 'ag-grid-react';

import {
  ColDef,
  GridApi,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  ModelUpdatedEvent,
  ICellRendererParams,
} from 'ag-grid-community';

import CenteredSpinner from '../../../shared/CenteredSpinner';
import OverflowTooltip from '../../../shared/OverflowTooltip';

import RunStatusBadge, { suppressTabToReasonChip } from './RunStatusBadge';
import { RUN_STATUSES } from './runHistoryStatus';
import {
  DEFAULT_PAGE_SIZE,
  IRunHistoryRow,
  RUN_HISTORY_ERROR_MESSAGE,
  RunHistoryError,
  RunHistoryFilterValueColumn,
  RunHistoryScope,
  RunHistorySortKey,
  CLIENT_SORT_KEYS,
  fetchFilterValues,
  fetchRunHistory,
} from './runHistoryApi';

const RESTRICTED = 'Restricted';

const toUtcIso = (agDate: string, nextDayStart: boolean) => {
  const date = new Date(agDate.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return undefined;
  if (nextDayStart) {
    date.setDate(date.getDate() + 1);
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

// Maps ag-grid's filter model onto the API's flat filter keys.
export const filterModelToFilters = (
  model: Record<string, any> | null | undefined
): Record<string, string> => {
  const filters: Record<string, string> = {};
  Object.entries(model ?? {}).forEach(([colId, m]) => {
    if (!m) return;
    if (colId === 'runTime') {
      const from = m.dateFrom && toUtcIso(m.dateFrom, false);
      // runTimeTo is exclusive and the picker selects whole days, so send the
      // start of the next local day.
      const to = m.dateTo && toUtcIso(m.dateTo, true);
      if (from) filters.runTimeFrom = from;
      if (to) filters.runTimeTo = to;
    } else if (typeof m.value === 'string') {
      filters[colId] = m.value;
    } else if (typeof m.filter === 'string') {
      filters[colId] = m.filter;
    }
  });
  return filters;
};

export const formatDuration = (ms: unknown): string => {
  if (typeof ms !== 'number' || !Number.isSafeInteger(ms) || ms < 0) return '';
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const sortModelToQuery = (
  sortModel: { colId: string; sort: string }[] | undefined
): { sort?: RunHistorySortKey; sortDir?: 'asc' | 'desc' } => {
  const first = sortModel?.[0];
  if (
    !first ||
    !(CLIENT_SORT_KEYS as readonly string[]).includes(first.colId) ||
    (first.sort !== 'asc' && first.sort !== 'desc')
  ) {
    return {};
  }
  return { sort: first.colId as RunHistorySortKey, sortDir: first.sort };
};

interface ISelectFilterProps extends CustomFilterProps {
  loadValues?: () => Promise<string[]>;
  values?: string[];
}

// The API takes one value per column, so the filter is a single select rather
// than a multi-value set filter.
const SingleSelectFilter: FunctionComponent<ISelectFilterProps> = ({
  model,
  onModelChange,
  loadValues,
  values = [],
}) => {
  // Filtering happens on the server.
  useGridFilter({ doesFilterPass: () => true });
  const [options, setOptions] = useState<string[]>(values);

  useEffect(() => {
    if (!loadValues) return;
    let active = true;
    loadValues()
      .then((loaded) => active && setOptions(loaded))
      .catch(() => active && setOptions([]));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ p: 1, minWidth: 180 }}>
      <Select
        size="small"
        fullWidth
        displayEmpty
        value={model?.value ?? ''}
        inputProps={{ 'aria-label': 'Filter value' }}
        onChange={(e) =>
          onModelChange(e.target.value ? { value: e.target.value } : null)
        }
      >
        <MenuItem value="">All</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

const sourceCell = (params: ICellRendererParams<IRunHistoryRow>) => {
  if (!params.data) return null;
  if (params.data.sourceRestricted) {
    return (
      <Typography
        component="span"
        variant="body2"
        sx={{ fontStyle: 'italic' }}
        title="Source restricted"
      >
        {RESTRICTED}
      </Typography>
    );
  }
  return <span>{params.value}</span>;
};

interface IRunHistoryGridProps {
  /** Takes precedence over `modelInstanceId`. */
  scope?: RunHistoryScope;
  modelInstanceId?: number;
  /** Applied before the first request; keyed by column id. */
  initialFilterModel?: Record<string, any>;
  /** Client scope only: opens the run detail for a Failed or Skipped row. */
  onViewRunDetail?: (row: IRunHistoryRow) => void;
  /** Controlled server page (1-based). */
  page?: number;
  /** Controlled server page size; one of `PAGE_SIZES`. */
  pageSize?: number;
  onTotal?: (total: number) => void;
  /** A filter or sort change moved the grid back to page 1. */
  onPageReset?: () => void;
  onGridApi?: (api: GridApi) => void;
}

// The API's maximum page size: one block always holds a whole server page.
const CACHE_BLOCK_SIZE = 100;

export const GRID_DENSITY_STYLE = {
  '--ag-font-size': '12px',
  '--ag-grid-size': '4px',
  '--ag-cell-horizontal-padding': '8px',
  '--ag-row-height': '28px',
} as React.CSSProperties;

const TARGET_COL_IDS = ['targetModel', 'targetModelInstance'];

const AUTOSIZE_COL_IDS = [
  'sourceModel',
  'sourceModelInstance',
  'scenarioTag',
  'subscriptionStatus',
  'runTime',
  'processingMs',
  'queueMs',
  'totalMs',
  'status',
];

const CLIENT_AUTOSIZE_COL_IDS = [...TARGET_COL_IDS, ...AUTOSIZE_COL_IDS];

const GRID_LABEL = 'Inter-Model Run History';

// Stable identity: a new object each render makes ag-grid reset the cache again.
const DEFAULT_COL_DEF: ColDef<IRunHistoryRow> = {
  resizable: false,
  suppressMovable: true,
  wrapHeaderText: true,
  autoHeaderHeight: true,
};

const RunHistoryGrid: FunctionComponent<IRunHistoryGridProps> = ({
  scope: scopeProp,
  modelInstanceId,
  initialFilterModel,
  onViewRunDetail,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  onTotal,
  onPageReset,
  onGridApi,
}) => {
  const clientId = scopeProp?.kind === 'client' ? scopeProp.clientId : null;
  const isClient = clientId !== null;
  const instanceId =
    scopeProp?.kind === 'instance'
      ? scopeProp.modelInstanceId
      : (modelInstanceId as number);
  // Primitive deps keep the scope (and so columnDefs and the datasource) stable.
  const scope = useMemo<RunHistoryScope>(
    () =>
      clientId !== null
        ? { kind: 'client', clientId }
        : { kind: 'instance', modelInstanceId: instanceId },
    [clientId, instanceId]
  );
  const [total, setTotal] = useState<number | null>(null);
  // null: no error. The message is one of the two fixed API constants.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // True from a request starting until the latest one settles.
  const [busy, setBusy] = useState(false);
  const apiRef = useRef<GridApi | null>(null);
  const onViewRunDetailRef = useRef(onViewRunDetail);
  onViewRunDetailRef.current = onViewRunDetail;
  // The preset is read once, at grid ready.
  const initialFilterModelRef = useRef(initialFilterModel);
  // True from setFilterModel(preset) until its onFilterChanged attaches the
  // datasource, so the first request already carries the preset.
  const awaitingPreset = useRef(false);
  const datasourceAttached = useRef(false);
  // Set by a successful datasource load, consumed by the next model update.
  const autosizePending = useRef(false);
  // Read by the datasource at request time, so the grid instance (and with it
  // the filter and sort state) survives page and page-size changes.
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  const onTotalRef = useRef(onTotal);
  onTotalRef.current = onTotal;
  // ag-grid drops a stale response's rows itself, but not our side effects
  // (total, error state, autosize): only the latest request may apply them.
  const latestRequest = useRef<IGetRowsParams | null>(null);

  useEffect(() => {
    // Compare with the refs, not the previous props: a filter or sort change
    // already reset pageRef to 1 and ag-grid already reloaded, so the parent's
    // follow-up page=1 must not purge (and request) a second time. Equal on
    // mount, so the initial load is never purged.
    if (page === pageRef.current && pageSize === pageSizeRef.current) return;
    pageRef.current = page;
    pageSizeRef.current = pageSize;
    // Destroys the current block, so an in-flight response for the old page
    // fails ag-grid's liveness check and is dropped.
    latestRequest.current = null;
    apiRef.current?.purgeInfiniteCache();
  }, [page, pageSize]);

  const columnDefs = useMemo<ColDef<IRunHistoryRow>[]>(() => {
    const textFilter = {
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        debounceMs: 400,
        buttons: ['reset'],
      },
    };
    const selectFilter = (
      loadValues?: () => Promise<string[]>,
      values?: string[]
    ) => ({
      filter: SingleSelectFilter,
      filterParams: { loadValues, values },
    });

    const valuesFilter = (column: RunHistoryFilterValueColumn) =>
      selectFilter(() => fetchFilterValues(scope, column));
    const presetTargetModel = initialFilterModelRef.current?.targetModel?.value;

    const targetColumns: ColDef<IRunHistoryRow>[] = isClient
      ? [
          {
            colId: 'targetModel',
            field: 'targetModel',
            headerName: 'Target Model',
            minWidth: 140,
            maxWidth: 320,
            tooltipField: 'targetModel',
            ...selectFilter(
              () => fetchFilterValues(scope, 'targetModel'),
              // Seeds the select so the preset shows as chosen.
              typeof presetTargetModel === 'string'
                ? [presetTargetModel]
                : undefined
            ),
          },
          {
            colId: 'targetModelInstance',
            field: 'targetModelInstance',
            headerName: 'Target Model\u00A0Instance',
            minWidth: 140,
            maxWidth: 320,
            tooltipField: 'targetModelInstance',
            ...valuesFilter('targetModelInstance'),
          },
        ]
      : [];

    const actionColumn: ColDef<IRunHistoryRow>[] = isClient
      ? [
          {
            colId: 'viewRunDetail',
            headerName: 'Actions',
            width: 80,
            sortable: false,
            filter: false,
            cellRenderer: (p: ICellRendererParams<IRunHistoryRow>) => {
              const data = p.data;
              if (
                !data ||
                (data.status !== 'Failed' && data.status !== 'Skipped')
              ) {
                return null;
              }
              const when = data.runTime
                ? ` at ${new Date(data.runTime).toLocaleString()}`
                : '';
              return (
                <IconButton
                  size="small"
                  aria-label={`View run detail: ${data.status} run${when}`}
                  onClick={() => onViewRunDetailRef.current?.(data)}
                >
                  <Visibility fontSize="inherit" />
                </IconButton>
              );
            },
          },
        ]
      : [];

    return [
      ...targetColumns,
      {
        colId: 'sourceModel',
        field: 'sourceModel',
        headerName: 'Source Submission Model',
        minWidth: 140,
        maxWidth: 320,
        tooltipField: 'sourceModel',
        cellRenderer: sourceCell,
        ...valuesFilter('sourceModel'),
      },
      {
        colId: 'sourceModelInstance',
        field: 'sourceModelInstance',
        headerName: 'Source Submission Model Instance',
        minWidth: 140,
        maxWidth: 320,
        tooltipField: 'sourceModelInstance',
        cellRenderer: sourceCell,
        ...valuesFilter('sourceModelInstance'),
      },
      {
        colId: 'scenarioTag',
        field: 'scenarioTag',
        headerName: 'Subscription Scenario Tag',
        minWidth: 140,
        maxWidth: 320,
        ...textFilter,
        // CLD-3040: full tag name on hover when it's clipped.
        cellRenderer: OverflowTooltip,
      },
      {
        colId: 'subscriptionStatus',
        field: 'subscriptionStatus',
        headerName: 'Subscription Status',
        minWidth: 110,
        ...selectFilter(undefined, ['Active', 'Inactive']),
      },
      {
        colId: 'runTime',
        field: 'runTime',
        headerName: 'Run Time',
        minWidth: 150,
        sort: 'desc',
        valueFormatter: (p) =>
          p.value ? new Date(p.value).toLocaleString() : '',
        filter: 'agDateColumnFilter',
        filterParams: { filterOptions: ['inRange'], maxNumConditions: 1 },
      },
      {
        colId: 'processingMs',
        field: 'processingMs',
        headerName: 'Processing Time',
        minWidth: 100,
        filter: false,
        valueFormatter: (p) => formatDuration(p.value),
      },
      {
        colId: 'queueMs',
        field: 'queueMs',
        headerName: 'Queue Time',
        minWidth: 100,
        filter: false,
        valueFormatter: (p) => formatDuration(p.value),
      },
      {
        colId: 'totalMs',
        field: 'totalMs',
        headerName: 'Total Time',
        minWidth: 100,
        filter: false,
        valueFormatter: (p) => formatDuration(p.value),
      },
      {
        colId: 'status',
        field: 'status',
        headerName: 'Status',
        minWidth: 110,
        cellRenderer: (p: ICellRendererParams<IRunHistoryRow>) =>
          p.data ? (
            <RunStatusBadge
              status={p.data.status}
              reasonCode={isClient ? p.data.reasonCode : undefined}
            />
          ) : null,
        suppressKeyboardEvent: suppressTabToReasonChip,
        ...selectFilter(undefined, [...RUN_STATUSES]),
      },
      ...actionColumn,
      {
        colId: 'detail',
        field: 'detail',
        headerName: 'Detail',
        flex: 2,
        minWidth: 240,
        sortable: false,
        tooltipField: 'detail',
        ...textFilter,
      },
    ];
  }, [scope, isClient]);

  const datasource = useMemo<IDatasource>(
    () => ({
      getRows: (params) => {
        latestRequest.current = params;
        const isLatest = () => latestRequest.current === params;
        const { filterModel, sortModel } = params;
        setBusy(true);
        fetchRunHistory(scope, {
          page: pageRef.current,
          pageSize: pageSizeRef.current,
          ...sortModelToQuery(sortModel),
          filters: filterModelToFilters(filterModel),
        })
          .then((result) => {
            if (isLatest()) {
              setErrorMessage(null);
              setTotal(result.total);
              onTotalRef.current?.(result.total);
              autosizePending.current = true;
              setBusy(false);
            }
            // Always called: ag-grid's block liveness check drops stale rows.
            // lastRow = items.length: the single block is exactly this server
            // page, so ag-grid never asks for a second block.
            params.successCallback(result.items, result.items.length);
          })
          .catch((error) => {
            if (isLatest()) {
              setErrorMessage(
                error instanceof RunHistoryError
                  ? error.message
                  : RUN_HISTORY_ERROR_MESSAGE
              );
              setBusy(false);
            }
            params.failCallback();
          });
      },
    }),
    [scope]
  );

  const handleGridReady = useCallback(
    (e: GridReadyEvent) => {
      apiRef.current = e.api;
      onGridApi?.(e.api);
      const preset = initialFilterModelRef.current;
      if (preset && Object.keys(preset).length > 0) {
        awaitingPreset.current = true;
        e.api.setFilterModel(preset);
      } else {
        datasourceAttached.current = true;
        e.api.setGridOption('datasource', datasource);
      }
    },
    [onGridApi, datasource]
  );

  // A scope change after the first attach swaps the datasource.
  useEffect(() => {
    if (datasourceAttached.current) {
      apiRef.current?.setGridOption('datasource', datasource);
    }
  }, [datasource]);

  const handleModelUpdated = useCallback(
    (e: ModelUpdatedEvent) => {
      if (!autosizePending.current) return;
      autosizePending.current = false;
      e.api.autoSizeColumns(
        isClient ? CLIENT_AUTOSIZE_COL_IDS : AUTOSIZE_COL_IDS,
        true
      );
    },
    [isClient]
  );

  // ag-grid resets the cache itself on filter or sort change; its getRows runs
  // in a later timeout than this (async) callback, so it reads page 1.
  const handleFilterOrSortChanged = useCallback(() => {
    if (awaitingPreset.current) {
      // The preset's own change: attach now, no extra request or page reset.
      awaitingPreset.current = false;
      datasourceAttached.current = true;
      apiRef.current?.setGridOption('datasource', datasource);
      return;
    }
    pageRef.current = 1;
    latestRequest.current = null;
    onPageReset?.();
  }, [onPageReset, datasource]);

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      {errorMessage !== null && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => apiRef.current?.refreshInfiniteCache()}
            >
              Retry
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      )}
      <Box
        role="region"
        aria-label={GRID_LABEL}
        className="ag-theme-alpine"
        sx={{ flex: 1, minHeight: 0, width: '100%', position: 'relative' }}
        style={GRID_DENSITY_STYLE}
      >
        {busy && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <CenteredSpinner />
          </Box>
        )}
        {/* One instance for the dialog's lifetime: no key, no ag-grid
            pagination. Paging is controlled through page / pageSize. */}
        <AgGridReact<IRunHistoryRow>
          reactiveCustomComponents
          rowModelType="infinite"
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          suppressColumnVirtualisation
          cacheBlockSize={CACHE_BLOCK_SIZE}
          maxBlocksInCache={1}
          suppressRowVirtualisation
          suppressContextMenu
          suppressNoRowsOverlay
          getRowId={(p) => p.data.rowId}
          onGridReady={handleGridReady}
          onModelUpdated={handleModelUpdated}
          onFilterChanged={handleFilterOrSortChanged}
          onSortChanged={handleFilterOrSortChanged}
        />
      </Box>
      {total === 0 && errorMessage === null && (
        <Typography
          role="status"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 2 }}
        >
          No run history found.
        </Typography>
      )}
    </Box>
  );
};

export default RunHistoryGrid;
